import React, { useState } from 'react';
import { isValidUKPhone, UK_PHONE_ERROR } from '../lib/phone';
import { FileDown, Loader2, CheckCircle, X } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const inputStyle = { width: '100%', padding: '12px 14px', border: '2px solid #e2e8f0', fontSize: 18, color: 'var(--dark)', background: '#fff', outline: 'none', boxSizing: 'border-box', marginBottom: 10 };

// Reusable gated download modal. `resource` = { slug, title }.
export const ResourceGateModal = ({ resource, onClose, source = '' }) => {
  const [form, setForm] = useState({ name: '', email: '', phone: '', job_title: '', org_name: '' });
  const [status, setStatus] = useState('idle');
  const [error, setError] = useState('');
  const [url, setUrl] = useState('');
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.phone.trim() || !form.job_title.trim() || !form.org_name.trim()) {
      setError('Please add your name, email, phone, job title and organisation');
      return;
    }
    if (!isValidUKPhone(form.phone)) { setError(UK_PHONE_ERROR); return; }
    setError('');
    setStatus('loading');
    try {
      const res = await fetch(`${API}/api/resource-download`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, resource_slug: resource.slug, source }),
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(d.detail || 'Something went wrong');
      const full = `${API}${d.download_url}`;
      setUrl(full);
      setStatus('done');
      window.open(full, '_blank', 'noopener');
    } catch (err) {
      setError(err.message || 'Something went wrong');
      setStatus('error');
    }
  };

  return (
    <div data-testid="resource-gate-modal" onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
      <div onClick={(e) => e.stopPropagation()} style={{ background: '#fff', borderRadius: 12, maxWidth: 440, width: '100%', padding: '30px 28px', position: 'relative' }}>
        <button data-testid="resource-gate-close" onClick={onClose} style={{ position: 'absolute', top: 14, right: 14, background: 'none', border: 'none', cursor: 'pointer', color: 'var(--body-text)' }}><X size={22} /></button>
        {status === 'done' ? (
          <div data-testid="resource-gate-success" style={{ textAlign: 'center' }}>
            <CheckCircle size={44} style={{ color: 'var(--teal)', marginBottom: 14 }} />
            <h3 style={{ fontFamily: 'var(--font-heading)', fontSize: 20, color: 'var(--dark)', marginBottom: 10 }}>Your download is ready</h3>
            <p style={{ fontSize: 18, color: 'var(--body-text)', lineHeight: 1.6, marginBottom: 18 }}>If it didn't open automatically, use the button below. We've also sent you a copy by email.</p>
            <a href={url} target="_blank" rel="noopener noreferrer" className="btn-pink-filled" data-testid="resource-gate-download-link"><FileDown size={15} /> DOWNLOAD PDF</a>
          </div>
        ) : (
          <form onSubmit={submit}>
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: 'var(--teal)', color: '#fff', padding: '5px 12px', borderRadius: 20, fontFamily: 'var(--font-heading)', fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 12 }}>Free download</div>
            <h3 style={{ fontFamily: 'var(--font-heading)', fontSize: 20, color: 'var(--dark)', marginBottom: 8, lineHeight: 1.25 }}>{resource.title}</h3>
            <p style={{ fontSize: 13, color: 'var(--body-text)', lineHeight: 1.6, marginBottom: 16 }}>Pop in your details and we'll send it straight over.</p>
            <input data-testid="resource-gate-name" style={inputStyle} placeholder="Your name *" value={form.name} onChange={set('name')} />
            <input data-testid="resource-gate-email" style={inputStyle} type="email" placeholder="Email address *" value={form.email} onChange={set('email')} />
            <input data-testid="resource-gate-phone" style={inputStyle} placeholder="Phone number *" value={form.phone} onChange={set('phone')} />
            <input data-testid="resource-gate-job-title" style={inputStyle} placeholder="Job title *" value={form.job_title} onChange={set('job_title')} />
            <input data-testid="resource-gate-org" style={inputStyle} placeholder="Organisation name *" value={form.org_name} onChange={set('org_name')} />
            {error && <p data-testid="resource-gate-error" style={{ color: '#e11d48', fontSize: 13, marginBottom: 10 }}>{error}</p>}
            <button data-testid="resource-gate-submit" type="submit" disabled={status === 'loading'} className="btn-pink-filled" style={{ border: 'none', cursor: status === 'loading' ? 'default' : 'pointer', width: '100%', justifyContent: 'center' }}>
              {status === 'loading' ? <Loader2 size={15} className="spin" /> : <FileDown size={15} />} GET THE FREE DOWNLOAD
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

export default ResourceGateModal;
